import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { AuthCard } from '@legacyorb/ui';
import { Button } from '@/components/ui/button';
import { Loader2, Smartphone, Apple } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import type { User } from '@/types';

interface OnboardingContext {
  user: User;
  setUser: (user: User) => void;
}

export function DownloadAppPage() {
  const navigate = useNavigate();
  const { user, setUser } = useOutletContext<OnboardingContext>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleComplete = async () => {
    setError('');
    setIsSubmitting(true);

    try {
      const response = await apiClient.post<{ onboardingCompleted: boolean }>(
        '/api/v1/onboarding/complete'
      );

      if (response.success) {
        setUser({ ...user, onboardingCompletedAt: new Date().toISOString() });
        navigate('/dashboard');
      } else {
        setError(response.error?.message || 'Failed to complete onboarding');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-theme flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-lg">
        <div className="mb-8 text-center">
          <Smartphone className="mx-auto mb-4 h-12 w-12 text-primary" />
          <h1 className="font-display text-3xl font-bold tracking-tight text-foreground">
            Get the App
          </h1>
          <p className="mt-2 text-muted-foreground">
            Download LegacyOrb on your mobile device for the best experience
          </p>
        </div>

        <AuthCard
          title="Download LegacyOrb"
          description="Available on iOS and Android"
          className="mb-6"
        >
          <div className="space-y-4">
            <a
              href="https://apps.apple.com/us/app/legacy-orb/id6761793026"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-lg border border-border p-4 transition-colors hover:bg-muted"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-foreground text-background">
                <Apple className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Download on the</p>
                <p className="text-lg font-semibold text-foreground">App Store</p>
              </div>
            </a>

            <a
              href="https://play.google.com/store/apps/details?id=com.legacyorb.app"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 rounded-lg border border-border p-4 transition-colors hover:bg-muted"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-foreground text-background">
                <Smartphone className="h-7 w-7" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Get it on</p>
                <p className="text-lg font-semibold text-foreground">Google Play</p>
              </div>
            </a>
          </div>
        </AuthCard>

        {error && <p className="mb-4 text-center text-sm text-destructive">{error}</p>}

        <div className="flex justify-center">
          <Button
            size="lg"
            onClick={handleComplete}
            disabled={isSubmitting}
            className="w-full max-w-md"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Continue to Dashboard'
            )}
          </Button>
        </div>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          You can always download the app later from your account settings.
        </p>
      </div>
    </div>
  );
}
